import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  setDocumentMeta, setCanonical, setOgImage, injectJsonLdList,
  buildWebPageSchema, buildFaqPageSchema, buildBreadcrumbSchema,
} from '@/lib/seo';
import { ContentPage } from '@/components/marketing/content-page';

const FAQS = [
  {
    q: 'What is help desk automation?',
    a: 'Help desk automation uses if-this-then-that rules to handle repetitive ticket work automatically. For example: "When a Critical ticket is created → assign to the on-call engineer, set status to In Progress." Rules fire on ticket events (created, status changed, SLA breached) and execute actions (set priority, assign, comment, notify, re-run AI) without human intervention.',
  },
  {
    q: 'How much does help desk automation cost?',
    a: 'Most vendors gate automation behind premium tiers: Zendesk advanced triggers require Suite Professional ($115/agent/month), Freshdesk full automations need Pro ($55/agent/month), Zoho Blueprint is $23–40/agent/month. Kora includes full automation rules free on every workspace — no per-seat fees, no tier gating.',
  },
  {
    q: 'What can help desk automation rules do?',
    a: 'Kora automation rules can: set ticket priority, change status, assign to team members, add comments, fire webhooks, send emails to workspace members, and re-run AI structuring. Triggers include ticket created, status/priority/assignee changed, comment added, SLA breached, and time-based conditions like "no response for 4 hours."',
  },
  {
    q: 'How do I test automation rules before enabling them?',
    a: 'Kora includes a built-in dry-run feature. When creating or editing a rule, click "Test rule" to see which of your last 20 tickets would match the conditions and which actions would fire — all without executing anything. Zero emails sent, zero webhooks fired, zero AI calls.',
  },
  {
    q: 'Can automation rules create loops?',
    a: 'No. Kora\'s automation engine has a built-in loop guard: actions performed by automation rules never trigger other rules. If a rule changes a ticket\'s status, that change doesn\'t fire the "status changed" trigger for other rules. This prevents infinite loops entirely.',
  },
  {
    q: 'What happens when a rule references a deleted snippet or removed team member?',
    a: 'The rule is automatically flagged with a "Needs attention" badge in Settings. The rule continues running but skips the broken action (logged as "skipped_snippet_deleted" or "skipped_member_inactive"). Editing the rule clears the flag once you fix or remove the broken reference.',
  },
  {
    q: 'Are automation rules free?',
    a: 'Yes. Kora automation rules are free on every workspace with no credit cost. The only metered action is AI structuring (capped at 50 free runs per workspace per day as a safety valve). All other triggers, conditions, and actions are unlimited within the rate caps (500 executions/hour per workspace).',
  },
  {
    q: 'How does help desk automation compare to Zapier or IFTTT?',
    a: 'Zapier and IFTTT are external tools that connect your help desk to other apps — they add per-task costs, latency, and a third-party dependency. Kora automation is built into the product: rules run instantly (inline with the ticket write), have no per-task cost, and can access workspace-native features like AI structuring, SLA recalculation, and role-aware assignment that external tools can\'t reach.',
  },
];

const COMPARISON = [
  { scenario: 'Critical ticket → auto-assign + escalate', kora: 'Free rule', zendesk: '$115/agent tier', freshdesk: '$55/agent tier' },
  { scenario: 'SLA breached → raise priority + notify', kora: 'Free rule', zendesk: '$115/agent tier', freshdesk: '$55/agent tier' },
  { scenario: 'No response in 4h → reminder email', kora: 'Free time trigger', zendesk: 'Not native', freshdesk: 'Limited' },
  { scenario: 'API ticket → auto-tag + route', kora: 'Free condition', zendesk: 'Custom code', freshdesk: 'Pro tier' },
  { scenario: 'Dry-run before enabling', kora: 'Built-in', zendesk: 'No', freshdesk: 'No' },
  { scenario: 'Run history per rule', kora: 'Built-in', zendesk: 'Enterprise', freshdesk: 'Pro tier' },
];

export function HelpdeskAutomationPage() {
  useEffect(() => {
    const title = 'Help Desk Automation Software — Free Rules | Kora';
    const description = 'Automate ticket work with triggers, conditions and actions: auto-assign, comment, notify, escalate on SLA breach. Free on every workspace, no per-seat fees.';
    setDocumentMeta(title, description);
    setCanonical('/helpdesk-automation');
    setOgImage('/assets/og-pricing.png');

    const cleanup = injectJsonLdList([
      buildWebPageSchema('/helpdesk-automation', title, description),
      buildBreadcrumbSchema([
        { name: 'Home', url: 'https://usekora.app/' },
        { name: 'Automation', url: 'https://usekora.app/helpdesk-automation' },
      ]),
      buildFaqPageSchema(FAQS),
    ]);

    return cleanup;
  }, []);

  return (
    <ContentPage
      pill="Feature guide"
      title="Help desk automation: stop doing repetitive ticket work"
      subtitle="If-this-then-that rules that listen to ticket events and act instantly. Set it up once, let it run forever — free on every workspace."
      answer={
        <>
          <strong>Kora automation rules are free and built-in.</strong> 12 triggers × 9 condition operators × 7 actions,
          with dry-run testing, run history, and rate caps. Competitors gate automation behind $49–115/agent/month tiers.
        </>
      }
      ctaTitle="Automate your first rule"
      ctaSubtitle="Free on every workspace. Set up in 2 minutes."
    >
      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mt-4 mb-3">
        Why automation matters
      </h2>
      <p className="text-sm text-zinc-600 leading-relaxed mb-4">
        Studies show 30–50% of B2B support tickets are automatable — routing, tagging, escalating, acknowledging.
        Every one of those is a manual click a human doesn't need to make. Yet most help desks charge premium-tier
        prices for the privilege: Zendesk's advanced triggers sit at $115/agent/month, Freshdesk's full automations
        at $55/agent/month.
      </p>
      <p className="text-sm text-zinc-600 leading-relaxed mb-6">
        Kora includes automation free. Not because it's a lesser version — because charging per-seat for
        "let the computer do the clicking" is the most per-seat thing in an already per-seat industry.
      </p>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-3">
        What you can build
      </h2>
      <div className="space-y-3 mb-6">
        {[
          { trigger: 'Critical ticket created', action: '→ Set In Progress + assign to on-call engineer' },
          { trigger: 'SLA breached', action: '→ Raise priority to Critical + email workspace admins' },
          { trigger: 'No response for 4 hours', action: '→ Add comment "Checking in — any update?" + notify assignee' },
          { trigger: 'Ticket from API', action: '→ Set priority High + add comment from triage snippet' },
          { trigger: 'Reporter from @bigclient.com', action: '→ Set priority Critical + assign to account manager' },
          { trigger: 'Status changed to Waiting for Team', action: '→ Fire webhook to Slack #escalations' },
        ].map((row) => (
          <div key={row.trigger} className="rounded-xl border border-zinc-200 p-3.5">
            <p className="text-xs font-semibold text-zinc-900 ">{row.trigger}</p>
            <p className="text-xs text-zinc-500 mt-1">{row.action}</p>
          </div>
        ))}
      </div>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-3">
        How it works
      </h2>
      <ul className="space-y-2 text-sm text-zinc-600 mb-6 list-disc pl-5">
        <li><strong>Triggers:</strong> ticket created, status/priority/assignee changed, comment added, ticket claimed/merged, SLA breached, no response for N hours, waiting for N hours</li>
        <li><strong>Conditions (ALL must match):</strong> category, priority, status, title contains, description contains, reporter email, reporter email domain, assignee, ticket source (dashboard/API/widget)</li>
        <li><strong>Actions:</strong> set priority (recalculates SLA), set status (validates workflow transitions), assign (mode-aware), add comment, fire webhook, send email to members, re-run AI structuring</li>
        <li><strong>Safety:</strong> dry-run testing, loop prevention, rate caps, soft-delete with preserved run history, kill switch</li>
      </ul>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-3">
        Kora vs per-seat vendors
      </h2>
      <div className="overflow-x-auto rounded-2xl border border-zinc-200 my-6">
        <table className="w-full min-w-[560px] text-sm bg-white ">
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50 ">
              <th className="text-left px-4 py-3 font-semibold text-zinc-900 ">Scenario</th>
              <th className="text-left px-4 py-3 font-semibold text-zinc-900 ">Kora</th>
              <th className="text-left px-4 py-3 font-semibold text-zinc-900 ">Zendesk</th>
              <th className="text-left px-4 py-3 font-semibold text-zinc-900 ">Freshdesk</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100 ">
            {COMPARISON.map((row) => (
              <tr key={row.scenario}>
                <td className="px-4 py-3 font-medium text-zinc-900 ">{row.scenario}</td>
                <td className="px-4 py-3 text-emerald-600 ">{row.kora}</td>
                <td className="px-4 py-3 text-zinc-600 ">{row.zendesk}</td>
                <td className="px-4 py-3 text-zinc-600 ">{row.freshdesk}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-3">
        Safety by design
      </h2>
      <ul className="space-y-2 text-sm text-zinc-600 mb-6 list-disc pl-5">
        <li><strong>Dry-run testing:</strong> see which tickets would match before enabling. Zero side effects.</li>
        <li><strong>Loop prevention:</strong> automation actions never trigger other rules — infinite loops impossible.</li>
        <li><strong>Rate caps:</strong> 500 runs/hour per workspace, 100/hour per rule, 50 AI actions/day. Skip events logged clearly.</li>
        <li><strong>SLA protection:</strong> automation comments never satisfy SLA first-response clocks.</li>
        <li><strong>Spam guard:</strong> automated emails go to workspace members only, never to unverified ticket reporters.</li>
        <li><strong>Kill switch + workspace toggle:</strong> disable all automation instantly without a code deploy.</li>
      </ul>

      <p className="text-sm text-zinc-600 mb-6">
        Related: <Link to="/internal-ticketing-software" className="underline text-zinc-900">internal ticketing software</Link>.
      </p>

      <h2 className="font-display text-xl md:text-2xl font-bold text-zinc-900 mb-4">
        Frequently asked questions
      </h2>
      <div className="divide-y divide-zinc-100 border-y border-zinc-100 mb-8">
        {FAQS.map((faq) => (
          <details key={faq.q} className="group py-3">
            <summary className="flex cursor-pointer list-none items-center justify-between text-sm font-medium text-zinc-900 ">
              {faq.q}
              <span className="text-zinc-400 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
            </summary>
            <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{faq.a}</p>
          </details>
        ))}
      </div>

      <div className="rounded-2xl border border-blue-100 bg-blue-50/50 p-5">
        <p className="text-sm text-zinc-700 leading-relaxed">
          Ready to automate? <Link to="/auth" className="font-medium text-blue-600 hover:underline">Create a free workspace</Link> and
          set up your first rule in Settings → Automation. Or see{' '}
          <Link to="/pricing" className="font-medium text-blue-600 hover:underline">how credits work</Link> —{' '}
          automation is always free.
        </p>
      </div>
    </ContentPage>
  );
}
